//
// Phone Number Formatter Controller
//

import { getElementString, setElementAttribute, onLoaded } from '../utilities'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

export default class PhoneNumberFormatterController {
  public static readonly shared = new PhoneNumberFormatterController()

  private countryCode: string | null = null

  // MARK: - Object Lifecycle

  private constructor() {
    this.updateCountryCode()
    onLoaded(this.setupObserver.bind(this))
  }

  // MARK: - Country Code

  private async fetchCountryCode(): Promise<string | null> {
    try {
      const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace')
      const body = await response.text()
      const lines = body.split('\n')
      const line = lines.find((line) => line.startsWith('loc='))
      if (!line) return null

      const location = line.split('=')[1]
      if (!location) return null

      return location.toUpperCase()
    } catch (error) {
      console.error('Trace request failed:', error)
      return null
    }
  }

  private async updateCountryCode(): Promise<void> {
    this.countryCode = await this.fetchCountryCode()

    if (!this.countryCode) {
      const countryCode = navigator.language.split('-')[1]
      this.countryCode = countryCode.toUpperCase()
    }

    onLoaded(() => this.formatPhoneNumberElements(document.body, true))
  }

  // MARK: - DOM Observation

  private setupObserver(): void {
    const observer = new MutationObserver(this.handleMutations.bind(this))
    observer.observe(document.body, { childList: true, subtree: true })
  }

  private handleMutations(mutations: MutationRecord[]): void {
    if (!this.countryCode) return

    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue

      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return

        this.formatPhoneNumberElements(node as HTMLElement, false)
      })
    }
  }

  // MARK: - Phone Number Formatting

  private formatPhoneNumber(value: string): string | null {
    try {
      const phoneNumber = parsePhoneNumberFromString(value)

      if (!phoneNumber || !phoneNumber.isValid()) {
        console.warn('Phone Number is not valid:', value)
        return null
      }

      return phoneNumber.format(phoneNumber.country === this.countryCode ? 'NATIONAL' : 'INTERNATIONAL')
    } catch (error) {
      console.error('Phone Number could not be formatted:', value, error)
      return null
    }
  }

  private formatPhoneNumberElement(element: HTMLElement): void {
    const phoneNumber = getElementString(element, 'data-phone-number')
    if (!phoneNumber) return

    setElementAttribute(element, 'data-phone-number-formatted', 'true')

    const formattedPhoneNumber = this.formatPhoneNumber(phoneNumber)
    if (!formattedPhoneNumber) return

    element.innerText = formattedPhoneNumber
  }

  private formatPhoneNumberElements(element: HTMLElement, force: boolean): void {
    let selector = '[data-phone-number]'
    if (!force) selector += ':not([data-phone-number-formatted="true"])'

    if (element.matches(selector)) {
      this.formatPhoneNumberElement(element)
    } else {
      element.querySelectorAll(selector).forEach((element) => {
        this.formatPhoneNumberElement(element as HTMLElement)
      })
    }
  }
}
